import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { loadPayengine } from 'payengine';
import { Loader2 } from 'lucide-react';
import * as mutations from '../../../graphql/mutations';
import * as queries from '../../../graphql/queries';
import { PageHeader } from '../../components/page-header';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import { useToast } from '../../components/ui/toast';
import featureFlag from '../../../application/common/featureFlag';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import { PE_CONFIG } from '../../../application/routes/payments/utils/config';
import { CreateMerchantForm } from './CreateMerchantForm';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
  appSettings?: {
    payEngineMerchantId?: string;
    payEngineMerchantStatus?: string;
  };
};

// ─── PayEngine Tab Wrapper ──────────────────────────────────────────────────

function PayEngineTab({
  type,
  viewType,
  merchantId,
  hmacHash,
}: {
  type: string;
  viewType?: string;
  merchantId: string;
  hmacHash: string;
}) {
  if (!hmacHash) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-[500px]">
      {/* @ts-ignore */}
      <pay-engine
        id={type}
        merchant-id={merchantId}
        hash={hmacHash}
        type={type}
        {...(viewType ? { 'view-type': viewType } : {})}
      >
        {/* @ts-ignore */}
      </pay-engine>
    </div>
  );
}

// ─── Onboarding State ───────────────────────────────────────────────────────

function OnboardingView({
  merchantId,
  hmacHash,
  canOnboard,
}: {
  merchantId: string;
  hmacHash: string;
  canOnboard: boolean;
}) {
  const payEngineRef = useRef<HTMLElement | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (payEngineRef.current) {
      const handleStepChange = (e: any) => {
        console.log('Onboarding form step change:', e.detail);
      };

      const handleSubmit = (e: any) => {
        console.log('Onboarding form submit success:', e.detail);
        toast.success('PayEngine onboarding form successfully submitted.');
      };

      const handleError = (e: any) => {
        console.error('Onboarding form error:', e.detail);
        toast.error(
          'There was an error submitting the onboarding form, please try again.'
        );
      };

      payEngineRef.current.addEventListener('stepChange', handleStepChange);
      payEngineRef.current.addEventListener('submitted', handleSubmit);
      payEngineRef.current.addEventListener('error', handleError);

      return () => {
        payEngineRef.current?.removeEventListener(
          'stepChange',
          handleStepChange
        );
        payEngineRef.current?.removeEventListener('submitted', handleSubmit);
        payEngineRef.current?.removeEventListener('error', handleError);
      };
    }
  }, [payEngineRef.current, toast]);

  if (!canOnboard) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center max-w-lg mx-auto">
        <p className="text-lg font-medium text-foreground mb-2">
          Onboarding Required
        </p>
        <p className="text-sm text-muted-foreground">
          Your organization needs to onboard for Payments still. Please have a
          user with a role that has the &apos;Onboard PayEngine&apos; permission
          login, visit the &apos;Payments&apos; tab, and fill in the onboarding
          form.
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full min-h-[500px] p-6">
      {/* @ts-ignore */}
      <pay-engine
        ref={payEngineRef}
        id="onboarding"
        merchant-id={merchantId}
        hash={hmacHash}
        type="boarding"
      >
        {/* @ts-ignore */}
      </pay-engine>
    </div>
  );
}

// ─── Main Payments Page ─────────────────────────────────────────────────────

export function PaymentsPage() {
  const navigate = useNavigate();
  const { checkPermission, hasAssignedRole } = usePermissions();
  const toast = useToast();

  const canOnboard = !!checkPermission(PermissionKeys.Sales_OnboardPayEngine);
  const viewPayments = !!checkPermission(PermissionKeys.Sales_ViewPayments);
  const hasRole = hasAssignedRole();

  const [merchantId, setMerchantId] = useState<string>(
    (global as any)?.appSettings?.payEngineMerchantId || ''
  );
  const [payEngineStatus, setPayEngineStatus] = useState<string>(
    (global as any)?.appSettings?.payEngineMerchantStatus || ''
  );
  const [hmacHash, setHmacHash] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Redirect if no access (only after permissions have loaded)
  useEffect(() => {
    if (!hasRole) return; // Permissions not loaded yet, wait
    if (!(featureFlag('payments') && viewPayments)) {
      navigate('/v2/pipeline', { replace: true });
    }
  }, [navigate, viewPayments, hasRole]);

  // Generate HMAC hash
  const generateHMAC = useCallback(
    async (merchantID: string) => {
      try {
        const res = await API.graphql(
          graphqlOperation(mutations.createPayEngineHash, {
            request: JSON.stringify({ merchantID }),
          })
        );
        // @ts-ignore
        const parsedData = JSON.parse(res.data.createPayEngineHash);
        return parsedData.hmac || '';
      } catch (err) {
        console.error(err);
        toast.error('There was an error verifying PayEngine credentials.');
        return '';
      }
    },
    [toast]
  );

  // Get merchant status
  const getMerchantStatus = useCallback(async (merchantID: string) => {
    try {
      const res = await API.graphql({
        // @ts-ignore
        query: queries.getPEOnboardingStatus,
        variables: {
          request: JSON.stringify({ merchantID }),
        },
      });
      // @ts-ignore
      const parsedData = JSON.parse(res.data.getPEOnboardingStatus);
      return parsedData.merchantStatus;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  // Update org PE status in DynamoDB
  const updateOrgPEStatus = useCallback(async (merchantStatus: string) => {
    try {
      const gqlTag: any = (await import('graphql-tag')).default;
      const myQuery = gqlTag`
        query GetOrg($id: ID!) {
          getOrg(id: $id) {
            id
            configParms
          }
        }
      `;
      const res = await API.graphql({
        // @ts-ignore
        query: myQuery,
        variables: { id: global.me.orgID },
      });
      // @ts-ignore
      const org = res.data.getOrg;
      const newConfigParms = {
        ...JSON.parse(org.configParms),
        payEngineMerchantStatus: merchantStatus,
      };

      const myMutation = gqlTag`
        mutation UpdateOrg($input: UpdateOrgInput!) {
          updateOrg(input: $input) {
            id
            configParms
          }
        }
      `;
      await API.graphql({
        // @ts-ignore
        query: myMutation,
        variables: {
          input: {
            id: org.id,
            configParms: JSON.stringify(newConfigParms),
          },
        },
      });
    } catch (err) {
      console.error('Error updating org PE status:', err);
    }
  }, []);

  // Initialize PayEngine
  useEffect(() => {
    async function init() {
      if (merchantId) {
        const hmac = await generateHMAC(merchantId);
        if (hmac) {
          setHmacHash(hmac);
        }

        if (payEngineStatus !== 'active') {
          const status = await getMerchantStatus(merchantId);
          if (status) {
            updateOrgPEStatus(status);
            setPayEngineStatus(status);
          }
        }
      }

      loadPayengine({ publicKey: PE_CONFIG.publicKey }).then(() => {});
      setLoading(false);
    }

    init();
  }, [merchantId]);

  // Handle merchant creation callback
  const handleMerchantCreated = useCallback((newMerchantId: string) => {
    setMerchantId(newMerchantId);
    if ((global as any).appSettings) {
      (global as any).appSettings.payEngineMerchantId = newMerchantId;
      (global as any).appSettings.payEngineMerchantStatus = 'editing';
    }
    setPayEngineStatus('editing');
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Payments" />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // No merchant profile yet - show creation form
  if (!merchantId) {
    if (!canOnboard) {
      return (
        <div className="flex flex-col h-full">
          <PageHeader title="Payments" />
          <div className="flex flex-col items-center justify-center flex-1 px-6 text-center max-w-lg mx-auto">
            <p className="text-lg font-medium text-foreground mb-2">
              Merchant Profile Required
            </p>
            <p className="text-sm text-muted-foreground">
              Your organization needs to create a merchant profile with
              PayEngine for Payments. Please have a user with a role that has
              the &apos;Onboard PayEngine&apos; permission login, visit the
              &apos;Payments&apos; tab, and fill in the Create PayEngine
              Merchant Profile form.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Payments" />
        <div className="flex-1 overflow-y-auto">
          <CreateMerchantForm onMerchantCreated={handleMerchantCreated} />
        </div>
      </div>
    );
  }

  // Merchant exists but not active - show onboarding
  if (merchantId && payEngineStatus !== 'active') {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Payments" />
        <div className="flex-1 overflow-y-auto">
          <OnboardingView
            merchantId={merchantId}
            hmacHash={hmacHash}
            canOnboard={canOnboard}
          />
        </div>
      </div>
    );
  }

  // Active merchant - show full payments interface
  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Payments" />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <Tabs defaultValue="transactions" className="w-full">
          <TabsList className="w-full bg-transparent border-b border-border rounded-none p-0 h-auto gap-2 sm:gap-6 overflow-x-auto">
            <TabsTrigger
              value="transactions"
              className="flex-none sm:flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-2 sm:px-0 pb-3 pt-2 font-bold text-xs sm:text-sm text-center whitespace-nowrap"
            >
              Transactions
            </TabsTrigger>
            <TabsTrigger
              value="batch-settlements"
              className="flex-none sm:flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-2 sm:px-0 pb-3 pt-2 font-bold text-xs sm:text-sm text-center whitespace-nowrap"
            >
              Batch Settlements
            </TabsTrigger>
            <TabsTrigger
              value="payouts"
              className="flex-none sm:flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-2 sm:px-0 pb-3 pt-2 font-bold text-xs sm:text-sm text-center whitespace-nowrap"
            >
              Payouts
            </TabsTrigger>
            <TabsTrigger
              value="merchant-statements"
              className="flex-none sm:flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-2 sm:px-0 pb-3 pt-2 font-bold text-xs sm:text-sm text-center whitespace-nowrap"
            >
              Merchant Statements
            </TabsTrigger>
            <TabsTrigger
              value="merchant-reports"
              className="flex-none sm:flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-2 sm:px-0 pb-3 pt-2 font-bold text-xs sm:text-sm text-center whitespace-nowrap"
            >
              Merchant Reports
            </TabsTrigger>
          </TabsList>

          <TabsContent value="transactions" className="mt-6">
            <PayEngineTab
              type="transactions"
              merchantId={merchantId}
              hmacHash={hmacHash}
            />
          </TabsContent>

          <TabsContent value="batch-settlements" className="mt-6">
            <PayEngineTab
              type="payouts"
              viewType="settlement"
              merchantId={merchantId}
              hmacHash={hmacHash}
            />
          </TabsContent>

          <TabsContent value="payouts" className="mt-6">
            <PayEngineTab
              type="payouts"
              viewType="funding"
              merchantId={merchantId}
              hmacHash={hmacHash}
            />
          </TabsContent>

          <TabsContent value="merchant-statements" className="mt-6">
            <PayEngineTab
              type="statements"
              merchantId={merchantId}
              hmacHash={hmacHash}
            />
          </TabsContent>

          <TabsContent value="merchant-reports" className="mt-6">
            <PayEngineTab
              type="merchant-reports"
              merchantId={merchantId}
              hmacHash={hmacHash}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
